<template>
  <div class="project-documents">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link to="/projects" class="breadcrumb-item">
        Projects
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link :to="`/projects/${projectId}`" class="breadcrumb-item">
        {{ project?.name || 'Project' }}
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">Document Intelligence</span>
    </div>

    <!-- Project Details Section -->
    <div class="project-header">
      <div class="project-info">
        <h2 class="page-title">{{ project?.name || 'Project Documents' }}</h2>
        <div class="project-metadata">
          <span class="project-type">{{ project?.project_type || 'Loading...' }}</span>
          <span class="project-description">{{ project?.description }}</span>
          <span v-if="project?.client_type" class="client-type">
            <i class="fas fa-building"></i> Client Type: {{ project.client_type }}
          </span>
        </div>
      </div>
      <div class="project-actions">
        <router-link :to="`/projects/${projectId}`" class="btn btn-outline-primary">
          <i class="fas fa-arrow-left"></i> Back to Project Hub
        </router-link>
        <router-link :to="`/projects/${projectId}/compliance`" class="btn btn-primary">
          <i class="fas fa-brain"></i> Compliance Intelligence
        </router-link>
      </div>
    </div>

    <!-- Loading and Error States -->
    <div v-if="loading" class="loading">Loading documents...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    
    <!-- Document Intelligence Hub -->
    <div v-if="!loading && !error" class="intelligence-hub">
      
      <!-- Intelligence Overview Section -->
      <div class="intelligence-overview">
        <div class="overview-header">
          <h2><i class="fas fa-brain"></i> Intelligence Overview</h2>
          <p class="overview-description">Two-phase intelligent document analysis workflow</p>
        </div>
        
        <div class="workflow-progress">
          <div class="phase-card contract-phase" :class="{ 'active': contractDocuments.length > 0 }">
            <div class="phase-icon">
              <i class="fas fa-file-contract"></i>
            </div>
            <div class="phase-content">
              <h3>Contract Intelligence</h3>
              <p>{{ contractDocuments.length }} contracts analyzed</p>
              <div class="phase-status">
                <span v-if="contractDocuments.length === 0" class="status pending">Ready to start</span>
                <span v-else class="status active">{{ extractedRequirements.length }} requirements extracted</span>
              </div>
            </div>
          </div>
          
          <div class="workflow-arrow">
            <i class="fas fa-arrow-right"></i>
          </div>
          
          <div class="phase-card rag-phase" :class="{ 'active': complianceDocuments.length > 0, 'disabled': contractDocuments.length === 0 }">
            <div class="phase-icon">
              <i class="fas fa-search"></i>
            </div>
            <div class="phase-content">
              <h3>COI, Endorsement & Policy Intelligence</h3>
              <p>{{ complianceDocuments.length }} documents analyzed</p>
              <div class="phase-status">
                <span v-if="contractDocuments.length === 0" class="status disabled">Requires contract analysis first</span>
                <span v-else-if="complianceDocuments.length === 0" class="status pending">Ready for document analysis</span>
                <span v-else class="status active">{{ Math.round(complianceRate * 100) }}% compliance rate</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Intelligence Sections (Simplified Flexbox) -->
      <div class="intelligence-sections-flex">
        <!-- Contract Intelligence Section -->
        <div class="intelligence-card contract-intelligence">
          <div class="section-header">
            <h2><i class="fas fa-file-contract"></i> Contract Intelligence</h2>
            <p class="section-description">Upload and analyze contracts to extract compliance requirements</p>
          </div>

          <div class="contract-actions">
            <div class="action-description">
              <p>Use the document management tools below to handle contract files and extract requirements.</p>
            </div>
            
            <div class="navigation-actions">
              <button @click="scrollToDocumentManagement" class="action-btn primary">
                <i class="fas fa-arrow-down"></i> Go to Document Management
              </button>
            </div>
            
            <!-- Contract Status Summary -->
            <div class="contract-status" v-if="contractDocuments.length > 0">
              <div class="status-card">
                <div class="status-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="status-content">
                  <h4>Analysis Complete</h4>
                  <p>{{ extractedRequirements.length }} requirements extracted</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- COI, Endorsement & Policy Intelligence Section -->
        <div class="intelligence-card coi-analysis">
          <div class="section-header">
            <h2><i class="fas fa-search"></i> COI, Endorsement & Policy Intelligence</h2>
            <p class="section-description">Analyze documents against extracted contract requirements</p>
          </div>

          <div class="rag-actions">
            <div class="action-description">
              <p>Request client documents and run COI, endorsement & policy analysis against contract requirements.</p>
            </div>
            
            <div class="navigation-actions">
              <router-link :to="`/projects/${projectId}/rag-analysis`" class="action-btn secondary">
                <i class="fas fa-search"></i> Open COI Analysis
              </router-link>
            </div>
            
            <!-- RAG Status Summary -->
            <div class="rag-status">
              <div class="status-card" :class="{ 'disabled': contractDocuments.length === 0 }">
                <div class="status-icon">
                  <i v-if="contractDocuments.length === 0" class="fas fa-lock"></i>
                  <i v-else-if="complianceDocuments.length === 0" class="fas fa-clock"></i>
                  <i v-else class="fas fa-check-circle"></i>
                </div>
                <div class="status-content">
                  <h4 v-if="contractDocuments.length === 0">Requires Contract Analysis</h4>
                  <h4 v-else-if="complianceDocuments.length === 0">Ready for Analysis</h4>
                  <h4 v-else>Analysis Complete</h4>
                  
                  <p v-if="contractDocuments.length === 0">Complete contract intelligence first</p>
                  <p v-else-if="complianceDocuments.length === 0">{{ extractedRequirements.length }} requirements ready</p>
                  <p v-else>{{ Math.round(complianceRate * 100) }}% compliance rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Workspace Section -->
      <div class="document-workspace-section">
        <div class="section-header">
          <h2><i class="fas fa-folder-open"></i> Document Workspace</h2>
          <p class="section-description">Upload, manage, and organize your project documents in one place.</p>
        </div>

        <!-- Unified Document Intelligence Actions -->
        <div class="document-intelligence-actions">
          <div class="actions-header">
            <h3><i class="fas fa-bolt"></i> Document Intelligence Actions</h3>
            <p>Upload documents directly, request files from clients, or analyze existing documents</p>
          </div>
          
          <div class="actions-grid">
            <!-- Direct Upload Card -->
            <div class="action-card upload-card">
              <div class="card-header">
                <h4><i class="fas fa-upload"></i> Direct Upload</h4>
                <p>Upload documents directly from your device</p>
              </div>
              
              <div class="enhanced-uploader">
                <div 
                  class="drop-zone" 
                  :class="{ 'active': isDragging, 'has-file': !!selectedFile }"
                  @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="handleFileDrop"
                  @click="triggerFileSelect"
                >
                  <input 
                    type="file" 
                    ref="fileInput"
                    @change="handleFileSelect" 
                    :disabled="isUploading"
                    class="hidden-file-input"
                  />
                  
                  <div v-if="!selectedFile" class="drop-message">
                    <i class="fas fa-cloud-upload-alt"></i>
                    <p>Drop file or click to browse</p>
                    <span class="file-hint">PDF, DOCX, TXT, etc.</span>
                  </div>
                  
                  <div v-else class="file-preview">
                    <div class="file-icon">
                      <i :class="getFileIcon(selectedFile.name)"></i>
                    </div>
                    <div class="file-info">
                      <span class="file-name">{{ selectedFile.name }}</span>
                      <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                    </div>
                    <button @click.stop="removeFile" class="remove-file">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                
                <div class="upload-options">
                  <button 
                    @click="uploadDocument" 
                    :disabled="!canUpload"
                    class="primary-button"
                    :class="{ 'uploading': isUploading }"
                  >
                    <i :class="isUploading ? 'fas fa-circle-notch fa-spin' : 'fas fa-upload'"></i>
                    {{ isUploading ? 'Uploading & Analyzing...' : 'Upload & Analyze' }}
                  </button>
                  
                  <!-- Classification Progress Indicator -->
                  <div v-if="classifying" class="classification-status">
                    <i class="fas fa-robot fa-spin"></i>
                    <span>AI classifying document...</span>
                  </div>
                </div>
                
                <div v-if="uploadProgress > 0 && isUploading" class="upload-progress-container">
                  <div class="upload-progress-bar" :style="{ width: uploadProgress + '%' }"></div>
                  <span class="upload-progress-text">{{ uploadProgress }}%</span>
                </div>
                
                <div v-if="uploadError" class="upload-error-message">
                  <i class="fas fa-exclamation-circle"></i>
                  {{ uploadError }}
                </div>
                
                <div v-if="uploadSuccess" class="upload-success-message">
                  <i class="fas fa-check-circle"></i>
                  File uploaded successfully!
                </div>
              </div>
            </div>

            <!-- Request Upload Card -->
            <div class="action-card request-card">
              <div class="card-header">
                <h4><i class="fas fa-envelope"></i> Request from Client</h4>
                <p>Send secure upload link to clients</p>
              </div>
              
              <div class="request-content">
                <button @click="toggleUploadRequestForm" class="toggle-button" :class="{ 'active': showUploadRequest }">
                  <i class="fas" :class="showUploadRequest ? 'fa-eye-slash' : 'fa-plus'"></i>
                  {{ showUploadRequest ? 'Hide Request Form' : 'Create Upload Request' }}
                </button>
                
                <div v-if="showUploadRequest" class="request-form-wrapper">
                  <UploadRequestForm 
                    :project-id="projectId"
                    :document-types="project?.allowed_document_types || []"
                    :workflows="workflows"
                    @request-sent="handleRequestSent"
                  />
                </div>
              </div>
            </div>

            <!-- Analysis Session Card -->
            <div class="action-card analysis-card">
              <div class="card-header">
                <h4><i class="fas fa-brain"></i> Analyze Documents</h4>
                <p>Run AI analysis on existing documents</p>
              </div>
              
              <div class="analysis-content">
                <div class="analysis-stats">
                  <div class="stat-item">
                    <span class="stat-number">{{ documents.length }}</span>
                    <span class="stat-label">Documents Available</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-number">{{ sessions.length }}</span>
                    <span class="stat-label">Analysis Sessions</span>
                  </div>
                </div>
                
                <button @click="toggleNewSessionForm" class="toggle-button" :class="{ 'active': showNewSessionForm }">
                  <i class="fas fa-plus"></i>
                  {{ showNewSessionForm ? 'Hide Analysis Form' : 'Create New Analysis' }}
                </button>
                
                <div v-if="showNewSessionForm" class="session-form-wrapper">
                  <AnalysisSessionManager
                    :project-id="projectId"
                    :documents="documents"
                    :workflows="workflows"
                    @session-created="handleSessionCreated"
                    @session-updated="handleSessionUpdated"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

          <!-- Pending Upload Requests -->
          <div class="card" v-if="pendingRequests && pendingRequests.length > 0">
            <h4>
              <i class="fas fa-clock"></i>
              Pending Upload Requests ({{ pendingRequests.length }})
            </h4>
            <div class="requests-grid">
              <div v-for="request in pendingRequests" 
                  :key="request.token" 
                  class="request-card">
                <div class="request-header">
                  <span class="request-email">{{ request.email }}</span>
                  <span class="request-date">{{ formatDate(request.created_at) }}</span>
                </div>
                <div class="request-details">
                  <div class="request-detail">
                    <i class="fas fa-link"></i>
                    <span>{{ request.token.substring(0, 8) }}...</span>
                  </div>
                  <div class="request-detail">
                    <i class="fas fa-calendar"></i>
                    <span>Expires: {{ formatDate(request.expires_at) }}</span>
                  </div>
                  <div class="request-detail" v-if="request.resend_count > 0">
                    <i class="fas fa-paper-plane"></i>
                    <span>Resent {{ request.resend_count }} time(s)</span>
                    <span class="resend-time">Last: {{ formatDate(request.last_resent_at) }}</span>
                  </div>
                  <NotificationHistory 
                    v-if="showingNotificationsFor === request.token"
                    :token="request.token" 
                  />
                </div>
                <div class="request-actions">
                  <button @click="copyUploadLink(request.token)" class="action-button">
                    <i class="fas fa-copy"></i> Copy Link
                  </button>
                  <button @click="resendUploadRequest(request.token)" class="action-button">
                    <i class="fas fa-paper-plane"></i> Resend
                  </button>
                  <button @click="revokeUploadRequest(request.token)" class="action-button delete">
                    <i class="fas fa-times"></i> Revoke
                  </button>
                  <button @click="toggleNotifications(request.token)" class="action-button">
                    <i class="fas fa-history"></i> History
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Management Area (Always visible when documents exist) -->
        <div v-if="documents.length > 0" class="document-management">
          <div class="subsection-header">
            <h3><i class="fas fa-file-alt"></i> Your Documents</h3>
            <div class="subsection-header-actions">
              <span class="document-count">{{ documents.length }} documents</span>
              <button
                v-if="largeDocCount > 0"
                @click="batchChunkProject"
                :disabled="batchChunking"
                class="batch-chunk-btn"
                :title="`Chunk all ${largeDocCount} large documents (20+ pages) in this project`"
              >
                <i :class="batchChunking ? 'fas fa-spinner fa-spin' : 'fas fa-layer-group'"></i>
                {{ batchChunking ? 'Chunking…' : `Chunk ${largeDocCount} Large Doc${largeDocCount !== 1 ? 's' : ''}` }}
              </button>
            </div>
          </div>
          
          <!-- Document List Component -->
          <DocumentList 
            :documents="documents"
            :loading="loading"
            @document-deleted="fetchDocuments"
          />
        </div>
      </div>
  </div>
</template>

<script>
import api from '@/services/api'; // Assuming this is the correct path
import axios from 'axios';
import auth from '@/services/auth';
import UploadRequestForm from './UploadRequestForm.vue'
import NotificationHistory from './NotificationHistory.vue'
import DocumentList from './DocumentList.vue'
import AnalysisSessionManager from './AnalysisSessionManager.vue'
import { errorHandlingMixin } from '@/mixins/errorHandling';



export default {
  name: 'ProjectDocuments',
  components: {
    UploadRequestForm,
    NotificationHistory,
    DocumentList,
    AnalysisSessionManager
  },
  mixins: [errorHandlingMixin],
  props: {
    id: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      project: null,
      documents: [],
      workflows: [],
      selectedWorkflows: {},
      selectedFile: null,
      selectedWorkflowForUpload: '',
      isUploading: false,
      error: null,
      selectedResults: null,
      loading: true,
      isRunningWorkflow: false,
      uploadError: null,
      uploadSuccess: false,
      classifying: false,
      classificationResult: null,
      uploadProgress: 0,
      filterStatus: 'ALL',
      showRunningWorkflows: false,
      runningWorkflowsCount: 0,
      showUploadRequest: false,
      sortBy: {
        field: 'created_at',
        direction: 'desc'
      },
      pendingRequests: [],
      pendingOperations: {},
      loadingRequests: false,
      showingNotificationsFor: null,
      uploadForm: {
        document_type: '',
        notes: '',
        workflow: null
      },
      documentTypes: [],
      sessions: [],
      activeSession: null,
      pollingInterval: null,
      activeAnalyses: new Set(),
      isModalVisible: false,
      analyses: [],
      isDevelopment: process.env.NODE_ENV === 'development',
      documentSearch: '',
      documentTypeFilter: '',
      isDragging: false,
      documentsFetched: false,
      batchChunking: false,
      showNewSessionForm: false,
      activeAnalysisSession: null,
      analysisSessions: [],
      loadingAnalysisSessions: false,
      isRefreshingSessions: false,
      
      // Intelligence Hub Data
      contractDocuments: [],
      complianceDocuments: [],
      extractedRequirements: [],
      selectedRagFile: null,
      ragUploading: false,
      isRagDragging: false,
      complianceRate: 0.71, // Mock data for now
      passingChecks: 15,
      failingChecks: 6,
      pendingChecks: 2,
    }
  },
  computed: {
    projectId() {
      const id = this.id || this.$route.params.id;
      console.log('Computed projectId:', id);
      return id;
    },

    largeDocCount() {
      return this.documents.filter(d => d.page_count && d.page_count > 20).length;
    },
    workflowAnalysisSessions() {
      if (!this.analysisSessions || this.analysisSessions.length === 0) {
        return [];
      }
      // Filter sessions to include only those that have at least one analysis of type 'WORKFLOW'
      return this.analysisSessions.filter(session => 
        session.analyses?.some(analysis => analysis.analysis_type === 'WORKFLOW')
      );
    },
    parsedResults() {
      if (!this.selectedResults?.results) return {};
      
      // If results is a string, try to parse it
      if (typeof this.selectedResults.results === 'string') {
        try {
          return JSON.parse(this.selectedResults.results);
        } catch (e) {
          console.error('Error parsing results:', e);
          return {};
        }
      }
      
      return this.selectedResults.results;
    },
    hasRunningWorkflows() {
      return this.runningWorkflowsCount > 0;
    },
    allRunningWorkflows() {
      return this.documents.flatMap(doc => 
        doc.workflow_runs.filter(run => 
          run.status === 'IN_PROGRESS' || run.status === 'PENDING'
        )
      );
    },
    sortedDocuments() {
      return [...this.documents].sort((a, b) => {
        const aValue = new Date(a[this.sortBy.field] || a.created_at);
        const bValue = new Date(b[this.sortBy.field] || b.created_at);
        
        if (this.sortBy.direction === 'desc') {
          return bValue - aValue;
        }
        return aValue - bValue;
      });
    },
    filteredSortedDocuments() {
      let filtered = this.sortedDocuments;

      if (this.documentSearch) {
        const searchLower = this.documentSearch.toLowerCase();
        filtered = filtered.filter(doc => 
          doc.name?.toLowerCase().includes(searchLower) ||
          doc.document_type?.toLowerCase().includes(searchLower) ||
          doc.uploaded_by_name?.toLowerCase().includes(searchLower) ||
          doc.uploaded_by_email?.toLowerCase().includes(searchLower)
        );
      }

      if (this.documentTypeFilter) {
        filtered = filtered.filter(doc => 
          doc.document_type === this.documentTypeFilter
        );
      }

      return filtered;
    },
    hasActiveFilters() {
      return this.documentSearch.length > 0 || this.documentTypeFilter.length > 0;
    },
    clientTypeSpecificText() {
      if (!this.project?.client_type) return '';
      
      switch(this.project.client_type.toLowerCase()) {
        case 'general contractor':
          return 'Analyze insurance requirements, indemnity clauses, and payment terms in contracts';
        case 'subcontractor':
          return 'Analyze scope of work, payment terms, and liability provisions in agreements';
        case 'owner':
          return 'Analyze property rights, warranties, and project responsibilities';
        case 'architect':
          return 'Analyze design responsibilities, professional liability, and service agreements';
        case 'supplier':
          return 'Analyze delivery terms, product specifications, and warranties';
        default:
          return 'Analyze client documents for specific requirements and terms';
      }
    },
    canUpload() {
      return this.selectedFile && !this.isUploading && !this.classifying;
    }
  },
  created() {
    console.log('ProjectDocuments component created');
    console.log('Project ID in created:', this.projectId);
    console.log('Props id:', this.id);
    this.initializeData();
    this.fetchSessions();
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      async handler(newId) {
        console.log('Route ID changed:', newId);
        if (newId) {
          try {
            this.loading = true;
            await this.fetchDocuments();
            await this.fetchWorkflows();
          } catch (error) {
            console.error('Error updating data:', error);
            this.error = 'Failed to update project data';
          } finally {
            this.loading = false;
          }
        }
      }
    },
    selectedWorkflows(newValue) {
      localStorage.setItem('selectedWorkflows', JSON.stringify(newValue));
    },
    documents: {
      handler() {
        this.updateRunningCount();
      },
      deep: true
    },
    projectId: {
      handler() {
        this.fetchDocuments();
        this.fetchPendingRequests();
      },
      immediate: true
    }
  },
  methods: {
    async initializeData() {
      try {
        this.loading = true;
        await this.fetchProjectDetails();
        await this.fetchDocuments();
        await this.fetchWorkflows();
        await this.fetchPendingRequests();
        await this.fetchSessions();
        await this.fetchAnalysisResults();
      } catch (error) {
        console.error('Error initializing data:', error);
        this.error = 'Failed to load project data';
      } finally {
        this.loading = false;
      }
    },
    handleRequestSent(result) {
      console.log('Upload request sent:', result);
      this.showUploadRequest = false;
      if (this.$toast) {
        if (result.success) {
          this.$toast.success(result.message || 'Upload request sent successfully');
        } else {
          this.$toast.error(result.message || 'Failed to send upload request');
        }
      }
    },
    async fetchProjectDetails() {
      try {
        const response = await api.get(`/projects/${this.projectId}/`);
        console.log('Project details full response:', response);
        
        // Handle wrapped response
        if (response.data) {
          if (response.data.project) {
            // If project details are under a 'project' key
            this.project = response.data.project;
            this.documentTypes = response.data.project.allowed_document_types || [];
          } else if (response.data.data) {
            // If response has nested data property
            this.project = response.data.data;
            this.documentTypes = response.data.data.allowed_document_types || [];
          } else {
            // If response data is direct
            this.project = response.data;
            this.documentTypes = response.data.allowed_document_types || [];
          }
          console.log('Processed project details:', this.project);
          console.log('Document types:', this.documentTypes);
          
          if (!this.documentTypes || !this.documentTypes.length) {
            console.warn('No document types found in project response:', response.data);
          }
        } else {
          console.error('Error response:', response);
          throw new Error('Failed to fetch project details');
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
        this.error = error.message || 'Failed to load project details';
      }
    },
    async batchChunkProject() {
      if (!confirm(`Start chunking all ${this.largeDocCount} large documents (20+ pages) in this project? This runs in the background and may take several minutes.`)) return;
      this.batchChunking = true;
      try {
        await api.post(`/projects/${this.projectId}/batch-chunk/`);
        if (this.$toast) {
          this.$toast.success(`Batch chunking started for ${this.largeDocCount} documents. Status will update in the background.`);
        } else {
          alert('Batch chunking started. Check individual documents for status updates.');
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to start batch chunking.';
        if (this.$toast) this.$toast.error(msg);
        else alert(msg);
      } finally {
        this.batchChunking = false;
      }
    },

    async fetchDocuments() {
      try {
        this.loading = true;
        // Use Axios get method
        const response = await api.get(`/projects/${this.projectId}/documents/`);
        console.log('Documents full response:', response); 
        
        // Access data from response.data
        const responseData = response.data;

        if (responseData) {
          if (responseData.documents) {
            this.documents = responseData.documents.map(doc => ({...doc, upload_method: doc.upload_method || 'MANUAL'}));
          } else if (Array.isArray(responseData)) {
            this.documents = responseData.map(doc => ({...doc, upload_method: doc.upload_method || 'MANUAL'}));
          } else if (responseData.data && Array.isArray(responseData.data)) { // Handle nested data if backend wraps it
            this.documents = responseData.data.map(doc => ({...doc, upload_method: doc.upload_method || 'MANUAL'}));
          } else {
            console.warn('Unexpected documents response structure:', responseData);
            this.documents = [];
          }
          console.log('Processed documents:', this.documents);
        } else {
          console.warn('No data in documents response:', response);
          this.documents = [];
        }
        this.documentsFetched = true;
      } catch (error) {
        // Error handling using message from interceptor
        console.error('Error fetching documents:', error); 
        this.error = error.message || 'Failed to load documents';
        this.documents = [];
        this.documentsFetched = true; // Still mark as fetched to avoid infinite loops
      } finally {
        this.loading = false;
      }
    },
    /**
     * LEGACY: Fetches WorkflowTemplates for backward compatibility.
     * The WorkflowTemplate/AutomatedCheck system is deprecated - use ComprehensiveCheckDefinition instead.
     * This is kept for existing UI dropdowns but should be removed.
     * See WORKSTREAMS.md "Legacy Code Cleanup" section.
     * TODO: Remove this and related workflow UI once confirmed no users depend on it.
     */
    async fetchWorkflows() {
      try {
        const response = await api.get('/workflow-templates/');
        console.log('[LEGACY] Raw workflows response:', response);
        this.workflows = response.data || [];
      } catch (error) {
        console.error('[LEGACY] Error fetching workflows:', error);
        // Don't show error to user for deprecated feature
        this.workflows = [];
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.uploadError = null;
        this.uploadSuccess = false;
      }
    },
    async uploadDocument() {
      if (!this.canUpload) return;

      this.isUploading = true;
      this.classifying = true;
      this.uploadError = null;

      const formData = new FormData();
      formData.append('file', this.selectedFile);
      // Removed: document_type - AI will classify automatically
      
      // Add selected workflows if available
      if (this.selectedWorkflows.length > 0) {
        formData.append('workflows', JSON.stringify(this.selectedWorkflows));
      }

      try {
        // Create a fresh axios instance for this upload to avoid global JSON headers
        const uploadApi = axios.create({
          baseURL: process.env.VUE_APP_API_URL || 'http://localhost:8000/api',
          headers: {
            Authorization: `Bearer ${auth.getToken()}`
          }
          // No Content-Type set - let axios auto-detect multipart with boundary
        });

        const response = await uploadApi.post(`/projects/${this.projectId}/documents/upload/`, formData);

        console.log('Upload response:', response);
        // Axios nests the actual response body under `data`
        console.log('Upload response data:', response.data);
        
        this.classifying = false;
        
        // Store classification result
        if (response.data?.classification) {
          this.classificationResult = response.data.classification;
          console.log('Classification result:', this.classificationResult);
        }

        // Refresh documents after successful upload
        await this.fetchDocuments();
        
        // Show success message with classification info
        if (this.$toast) {
          let message = 'Document uploaded successfully!';
          if (this.classificationResult) {
            const docType = this.formatDocumentType(this.classificationResult.primary_type);
            message += ` Classified as: ${docType}`;
          }
          this.$toast.success(message);
        }
        
        // Don't reset immediately - let user see classification
        setTimeout(() => {
          this.resetUploadForm();
        }, 3000);

      } catch (error) {
        this.classifying = false;
        
        // The error object should now be an Error instance with the message from the interceptor
        console.error('Upload error caught in component:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        console.error('Error message:', error.message);
        
        // Try to get detailed error message
        let errorMessage = 'Failed to upload document';
        
        // Handle classification rejection specifically
        if (error.response?.data?.rejection_reason) {
          errorMessage = `Document rejected: ${error.response.data.rejection_reason}`;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.uploadError = errorMessage;
        
        // Show error message using Toast
        if (this.$toast) {
          this.$toast.error(this.uploadError);
        }
      } finally {
        this.isUploading = false;
      }
    },
    resetUploadForm() {
      this.removeFile();
      this.classificationResult = null;
      this.uploadForm = {
        notes: '',
        workflow: null
      };
    },
    formatDocumentType(type) {
      if (!type) return 'Unknown';
      return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    getWorkflowRun(doc, workflowId) {
      console.log(`\nLooking for workflow ${workflowId} in document ${doc.name}:`, {
        doc_id: doc.id,
        workflow_runs: doc.workflow_runs,
        workflow_runs_length: doc.workflow_runs?.length
      });
      
      const run = doc.workflow_runs?.find(run => {
        console.log(`Checking run:`, {
          run_id: run.id,
          workflow_id: run.workflow_id,
          matches: run.workflow_id === workflowId
        });
        return run.workflow_id === workflowId;
      });
      
      console.log(`Found run for workflow ${workflowId}:`, run);
      return run;
    },
    canRerun(doc, workflowId) {
      const run = this.getWorkflowRun(doc, workflowId);
      return run && (run.status === 'FAILED' || run.status === 'COMPLETED');
    },
    async runWorkflow(document) {
      try {
        if (!document.id || !this.selectedWorkflows[document.id]) {
          console.error('Missing document ID or workflow selection');
          return;
        }

        const response = await api.post('/workflows/run/', {
          workflow_id: this.selectedWorkflows[document.id],
          document_id: document.id
        });

        // Handle both new and existing workflow runs
        const workflowRunId = response.workflow_run_id;
        if (workflowRunId) {
          this.pollWorkflowResults(workflowRunId);
          await this.fetchDocuments(); // Immediate refresh
          this.refreshWorkflowStatus(); // Start periodic refresh
        }
      } catch (error) {
        console.error('Error running workflow:', error);
        if (error.response?.status === 409) {
          this.$toast.info('A workflow is already running for this document');
          // Start polling the existing workflow
          const workflowRunId = error.response.data.workflow_run_id;
          if (workflowRunId) {
            this.pollWorkflowResults(workflowRunId);
          }
        } else {
          this.error = error.message || 'Failed to run workflow';
        }
      }
    },
    async pollWorkflowResults(workflowRunId) {
      try {
        const response = await api.get(`/workflows/results/${workflowRunId}/`);
        if (response.status === 'COMPLETED' || response.status === 'FAILED') {
          // Workflow finished, refresh document list
          await this.fetchDocuments();
        } else if (response.status === 'IN_PROGRESS' || response.status === 'PENDING' || response.status === 'PROCESSING') {
          // Continue polling
          setTimeout(() => this.pollWorkflowResults(workflowRunId), 2000);
        }
      } catch (error) {
        console.error('Error polling workflow results:', error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    getDocumentUrl(doc) {
      // Use unified document URL helper
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      return getDocumentUrl(doc);
    },
    viewDocument(document) {
      const url = this.getDocumentUrl(document);
      if (url && url !== '#') {
        console.log('Opening document URL:', url);
        window.open(url, '_blank');
      } else {
        console.error('Could not generate document URL for:', document);
        this.$toast.error('Unable to view document - file not found');
      }
    },

    async deleteDocument(docId) {
      if (!confirm('Are you sure you want to delete this document?')) {
        return;
      }
      
      try {
        this.loading = true;
        console.log(`Attempting to delete document ${docId}`);
        await api.delete(`/projects/${this.projectId}/documents/${docId}/`);
        console.log(`Document ${docId} deleted successfully`);
        
        // Remove the document from the local array
        this.documents = this.documents.filter(d => d.id !== docId);
        
        // Show success message
        this.error = null;
      } catch (error) {
        console.error('Error deleting document:', error);
        this.error = `Failed to delete document: ${error.message}`;
      } finally {
        this.loading = false;
      }
    },
    hasResults(run) {
      if (!run) {
        console.log('No run provided to hasResults');
        return false;
      }
      const hasResults = !!run.results;
      console.log(`Run ${run.id} has results: ${hasResults}`);
      return hasResults;
    },
    formatResults(results) {
      if (results.error) {
        return results.raw_response;
      }
      if (typeof results === 'string') {
        return results;
      }
      return JSON.stringify(results, null, 2);
    },
    // Add a helper method to refresh documents
    async refreshDocuments() {
      try {
        await this.fetchDocuments();
        console.log("Documents refreshed after workflow completion");
      } catch (error) {
        console.error("Error refreshing documents:", error);
      }
    },
    filteredWorkflowRuns(runs) {
      if (this.filterStatus === 'ALL') return runs;
      if (this.filterStatus === 'RUNNING') {
        return runs.filter(run => 
          run.status === 'IN_PROGRESS' || run.status === 'PENDING'
        );
      }
      return runs.filter(run => run.status === this.filterStatus);
    },
    calculateProgress(run) {
      if (!run.steps?.length) return 0;
      const completed = run.steps.filter(s => 
        s.status === 'COMPLETED'
      ).length;
      return Math.round((completed / run.steps.length) * 100);
    },
    async cancelWorkflow(runId) {
      try {
        const response = await api.post(`/workflows/${runId}/cancel/`);
        if (response.message) {
          this.$toast.success('Workflow cancelled');
          await this.fetchDocuments();
        } else {
          throw new Error(response.error || 'Failed to cancel workflow');
        }
      } catch (error) {
        console.error('Error cancelling workflow:', error);
        const errorMessage = error.response?.data?.error 
          || error.response?.data?.detail 
          || error.message 
          || 'Failed to cancel workflow';
        this.$toast.error(errorMessage);
        await this.fetchDocuments();
      }
    },
    updateRunningCount() {
      this.runningWorkflowsCount = this.allRunningWorkflows.length;
    },
    getStatusIcon(status) {
      switch (status) {
        case 'PENDING': return 'fa-clock';
        case 'IN_PROGRESS': return 'fa-spinner fa-spin';
        case 'PROCESSING': return 'fa-spinner fa-spin';
        case 'COMPLETED': return 'fa-check';
        case 'FAILED': return 'fa-times';
        case 'TIMEOUT': return 'fa-clock-o';
        default: return 'fa-question';
      }
    },
    getStepStatusIcon(status) {
      switch (status) {
        case 'PENDING': return 'fa-clock';
        case 'IN_PROGRESS': return 'fa-spinner fa-spin';
        case 'PROCESSING': return 'fa-spinner fa-spin';
        case 'COMPLETED': return 'fa-check';
        case 'FAILED': return 'fa-times';
        case 'SKIPPED': return 'fa-forward';
        default: return 'fa-question';
      }
    },
    formatStepDuration(step) {
      if (!step.started_at) return '';
      const start = new Date(step.started_at);
      const end = step.completed_at ? new Date(step.completed_at) : new Date();
      const seconds = Math.floor((end - start) / 1000);
      return `${seconds}s`;
    },
    async refreshWorkflowStatus() {
      await this.fetchDocuments();
      // If there are running workflows, schedule next refresh
      if (this.hasRunningWorkflows) {
        setTimeout(() => this.refreshWorkflowStatus(), 2000);
      }
    },
    formatDateTime(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleString();
    },
    calculateDuration(start, end) {
      const startTime = new Date(start);
      const endTime = new Date(end);
      const seconds = Math.floor((endTime - startTime) / 1000);
      
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
      return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    },
    async fetchPendingRequests() {
      try {
        this.loadingRequests = true;
        const response = await api.get(`/projects/${this.projectId}/upload-requests/`);
        this.pendingRequests = response.requests || [];
      } catch (error) {
        console.error('Error fetching pending requests:', error);
      } finally {
        this.loadingRequests = false;
      }
    },
    async copyUploadLink(token) {
      const link = `${window.location.origin}/secure-upload/${token}`;
      try {
        await navigator.clipboard.writeText(link);
        this.$toast.success('Upload link copied to clipboard');
      } catch (err) {
        console.error('Failed to copy:', err);
        this.$toast.error('Failed to copy link');
      }
    },
    async revokeUploadRequest(token) {
      try {
        await api.delete(`/upload-requests/${token}/`);
        this.$toast.success('Upload request revoked');
        await this.fetchPendingRequests();
      } catch (error) {
        console.error('Error revoking request:', error);
        this.$toast.error('Failed to revoke request');
      }
    },
    async resendUploadRequest(token) {
      try {
        const response = await api.post(`/upload-requests/${token}/resend/`);
        if (response) {
          this.$toast.success('Upload request resent successfully');
          await this.fetchPendingRequests();
        }
      } catch (error) {
        this.$toast.error('Failed to resend request');
      }
    },
    toggleNotifications(token) {
      this.showingNotificationsFor = token;
    },
    async handleUploadSuccess(documentData) {
      console.log('Document upload successful:', documentData);
      try {
        await this.fetchDocuments();
      } catch (error) {
        console.error('Error refreshing documents after upload:', error);
      }
    },
    handleWorkflowStarted() {
      this.fetchDocuments();
    },
    async fetchSessions() {
      console.log('Fetching sessions for project:', this.projectId);
      this.loadingAnalysisSessions = true;
      
      try {
        const response = await api.get(`/projects/${this.projectId}/analysis-sessions/`);
        console.log('Sessions API response:', response);
        
        if (response && response.data) {
          console.log('Sessions response data type:', typeof response.data);
          
          // Check for different response formats and handle appropriately
          if (Array.isArray(response.data)) {
            console.log('API returned an array directly');
            this.sessions = response.data;
            this.analysisSessions = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            console.log('API returned nested data array');
            this.sessions = response.data.data;
            this.analysisSessions = response.data.data;
          } else if (typeof response.data === 'object') {
            // Try to find any array in the response
            const possibleArrays = Object.keys(response.data)
              .filter(key => Array.isArray(response.data[key]));
            
            if (possibleArrays.length > 0) {
              // Use the first array found (or the one with the most items)
              const arrayKey = possibleArrays[0];
              console.log(`Found array at key '${arrayKey}'`);
              this.sessions = response.data[arrayKey];
              this.analysisSessions = response.data[arrayKey];
            } else {
              console.warn('No array found in response. Raw data:', response.data);
              this.sessions = [];
              this.analysisSessions = [];
            }
          }
          
          // Add detailed logging about session analyses and document IDs
          if (this.sessions && this.sessions.length > 0) {
            console.log('Fetched sessions count:', this.sessions.length);
            
            // Log details of each session's analyses
            this.sessions.forEach((session, index) => {
              if (session.analyses && session.analyses.length > 0) {
                console.log(`Session #${index + 1} (ID: ${session.id}) analyses:`, 
                  session.analyses.map(a => ({
                    analysisId: a.id,
                    documentId: a.document_id,
                    documentIdType: typeof a.document_id,
                    status: a.status,
                    has_results: !!a.results,
                    results_type: a.results ? typeof a.results : 'none'
                  }))
                );
                
                // Log a sample of the first analysis with results, if any
                const analysisWithResults = session.analyses.find(a => a.results);
                if (analysisWithResults) {
                  console.log('Sample analysis results:', {
                    analysisId: analysisWithResults.id,
                    results_type: typeof analysisWithResults.results,
                    results_preview: typeof analysisWithResults.results === 'object' ? 
                      JSON.stringify(analysisWithResults.results).substring(0, 200) + '...' : 
                      String(analysisWithResults.results).substring(0, 200) + '...'
                  });
                } else {
                  console.log('No analyses with results found in this session');
                }
              } else {
                console.log(`Session #${index + 1} (ID: ${session.id}) has no analyses`);
              }
            });
          } else {
            console.log('No sessions found in response');
          }
        } else {
          console.warn('Invalid API response format');
          this.sessions = [];
          this.analysisSessions = [];
        }
      } catch (error) {
        // ... existing error handling code ...
        console.error('Error fetching sessions:', error);
        
        // Handle different types of errors
        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
          console.warn('Network error - server may be unavailable');
          if (this.$toast) {
            this.$toast.error('Network error: Could not connect to server. Please check your connection and try again.');
          }
        } else if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error(`Server error ${error.response.status}:`, error.response.data);
          this.handleError(error, `Error ${error.response.status}: Failed to fetch analysis sessions`);
        } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received:', error.request);
          if (this.$toast) {
            this.$toast.error('Server timeout: No response received from server');
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          this.handleError(error, 'Failed to fetch analysis sessions');
        }
        
        this.sessions = [];
        this.analysisSessions = [];
      } finally {
        this.loadingAnalysisSessions = false;
      }
    },
    async handleSessionCreated(session) {
      try {
        console.log('New session created:', session);
        // Add to analysisSessions array
        this.analysisSessions.push(session);
        // Hide the form
        this.showNewSessionForm = false; 
        // Set as active session
        this.activeAnalysisSession = session;
        
        // Refresh the sessions list
        await this.fetchSessions();
        if (this.$toast) {
          this.$toast.success('Analysis session created successfully');
        }
      } catch (error) {
        console.error('Error handling new session:', error);
        this.handleError(error, 'Error updating sessions');
      }
    },
    async handleSessionUpdated(session) {
      try {
        // Update in analysisSessions array
        const analysisIndex = this.analysisSessions.findIndex(s => s.id === session.id);
        if (analysisIndex !== -1) {
          this.analysisSessions.splice(analysisIndex, 1, session);
        }
        
        // If this is the active analysis session, update it
        if (this.activeAnalysisSession?.id === session.id) {
          this.activeAnalysisSession = session;
        }
        
        // Update the session in the local list
        const index = this.sessions.findIndex(s => s.id === session.id);
        if (index !== -1) {
          this.sessions[index] = session;
        }
        
        // If this is the active session, update it
        if (this.activeSession?.id === session.id) {
          this.activeSession = session;
        }
      } catch (error) {
        console.error('Error updating session:', error);
        this.handleError(error, 'Error updating session');
      }
    },
    handleCheckSettingsUpdated(data) {
      console.log('Project check settings updated:', data);
      // Optionally refresh project data to reflect the new settings
      this.fetchProjectDetails();
      
      // Show notification if toast is available
      if (this.$toast) {
        this.$toast.success('Project comprehensive check settings updated successfully');
      }
    },
    startPolling(analysisId) {
      this.activeAnalyses.add(analysisId);
      if (!this.pollingInterval) {
        this.pollingInterval = setInterval(this.checkAnalysisStatus, 5000);
      }
    },
    async checkAnalysisStatus() {
      if (this.activeAnalyses.size === 0) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
        return;
      }

      try {
        const response = await api.get(`/projects/${this.projectId}/analysis-sessions/`);
        const sessions = response.data;
        
        // Update analyses status
        sessions.forEach(session => {
          session.analyses.forEach(analysis => {
            if (analysis.status === 'COMPLETED' || analysis.status === 'FAILED' || analysis.status === 'TIMEOUT') {
              this.activeAnalyses.delete(analysis.id);
            }
          });
        });

        // Update the sessions data
        this.sessions = sessions;
      } catch (error) {
        console.error('Error checking analysis status:', error);
      }
    },
    async startAnalysis(document, workflow) {
      try {
        const response = await api.post(`/projects/${this.projectId}/analysis-sessions/`, {
          name: `Analysis Session ${new Date().toLocaleDateString()}`,
          project: parseInt(this.projectId),
          status: 'DRAFT',
          documents: [{
            document_id: document.id,
            workflow_id: workflow.id
          }]
        });

        // Start polling for this analysis
        if (response.data.returndict?.analyses) {
          response.data.returndict.analyses.forEach(analysis => {
            this.startPolling(analysis.id);
          });
        }

        // Refresh sessions list
        await this.fetchSessions();
      } catch (error) {
        console.error('Error starting analysis:', error);
        this.$toast.error('Error starting analysis');
      }
    },
    toggleUploadRequestForm() {
      this.showUploadRequest = !this.showUploadRequest;
    },
    // Method to display analysis information
    displayAnalysisInfo(session) {
      if (session.status === 'COMPLETED') {
        return session.results || 'No results available';
      } else if (session.status === 'IN_PROGRESS' || session.status === 'PROCESSING') {
        return 'Analysis in progress...';
      } else if (session.status === 'ERROR' || session.status === 'FAILED') {
        return `Error: ${session.error_message}`;
      }
      return 'No analysis information available';
    },
    
    // ==================== INTELLIGENCE HUB METHODS ====================
    
    // Contract Intelligence Methods
    handleContractDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },
    
    handleContractSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },
    
    async uploadContract() {
      if (!this.selectedFile) return;
      
      try {
        this.uploading = true;
        
        // Mock contract analysis - in real app, this would call your contract analysis API
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate analysis time
        
        // Add to contract documents
        this.contractDocuments.push({
          id: Date.now(),
          name: this.selectedFile.name,
          type: 'contract',
          analyzed_at: new Date()
        });
        
        // Mock extracted requirements
        const mockRequirements = [
          {
            id: 1,
            title: 'Primary and Noncontributory Coverage',
            description: 'General liability insurance must be primary and noncontributory to any other insurance.',
            type: 'insurance_requirement'
          },
          {
            id: 2,
            title: 'Additional Insured Status',
            description: 'Client must be named as additional insured on all applicable policies.',
            type: 'insurance_requirement'
          },
          {
            id: 3,
            title: 'Minimum Coverage Limits',
            description: 'General liability coverage must be at least $2,000,000 per occurrence.',
            type: 'coverage_limit'
          }
        ];
        
        this.extractedRequirements.push(...mockRequirements);
        
        this.selectedFile = null;
        this.$toast.success('Contract analyzed successfully! Requirements extracted.');
        
      } catch (error) {
        console.error('Contract analysis error:', error);
        this.$toast.error('Failed to analyze contract. Please try again.');
      } finally {
        this.uploading = false;
      }
    },
    
    // COI Analysis Methods
    handleRagDrop(e) {
      e.preventDefault();
      this.isRagDragging = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.selectedRagFile = files[0];
      }
    },
    
    handleRagSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.selectedRagFile = files[0];
      }
    },
    
    clearRagFile() {
      this.selectedRagFile = null;
    },
    
    async uploadForCoiAnalysis() {
      if (!this.selectedRagFile) return;
      
      try {
        this.ragUploading = true;
        
        // Mock COI analysis - in real app, this would call your COI analysis API
        await new Promise(resolve => setTimeout(resolve, 3000)); // Simulate analysis time
        
        // Add to compliance documents
        this.complianceDocuments.push({
          id: Date.now(),
          name: this.selectedRagFile.name,
          type: 'compliance_document',
          analyzed_at: new Date(),
          compliance_rate: Math.random() * 0.4 + 0.6 // Mock compliance rate
        });
        
        // Update overall compliance metrics (mock)
        this.complianceRate = this.complianceDocuments.reduce((sum, doc) => sum + doc.compliance_rate, 0) / this.complianceDocuments.length;
        
        this.selectedRagFile = null;
        this.$toast.success('COI analysis completed! Compliance gaps identified.');
        
      } catch (error) {
        console.error('COI analysis error:', error);
        this.$toast.error('Failed to run COI analysis. Please try again.');
      } finally {
        this.ragUploading = false;
      }
    },
    
    scrollToDocumentManagement() {
      // Find the document workspace section
      const docSection = document.querySelector('.document-workspace-section');
      if (docSection) {
        // Smooth scroll to the section
        docSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      } else {
        console.warn('Document workspace section not found for scrolling');
      }
    },
    
    // ==================== END INTELLIGENCE HUB METHODS ====================
    
    async fetchAnalysisResults() {
      try {
        const response = await api.get(`/projects/${this.projectId}/analysis/`);
        console.log('Raw API response:', response);
        // Check if response has data property
        if (response.data) {
          // Filter out comprehensive analyses, as they should be shown in the comprehensive dashboard
          this.analyses = response.data.filter(analysis => 
            !analysis.analyses?.some(docAnalysis => docAnalysis.analysis_type === 'COMPREHENSIVE')
          );
          console.log('Filtered analyses data:', this.analyses);
        } else {
          console.warn('No data property in analysis response:', response);
          this.analyses = [];
        }
      } catch (error) {
        console.error('Error fetching analyses:', error);
        this.analyses = [];
      }
    },
    getDocumentName(documentId) {
      const document = this.documents.find(d => d.id === documentId);
      return document ? document.name : `Document ${documentId}`;
    },
    toggleSortDirection() {
      this.sortBy.direction = this.sortBy.direction === 'desc' ? 'asc' : 'desc';
    },
    resetFilters() {
      this.documentSearch = '';
      this.documentTypeFilter = '';
      this.sortBy = {
        field: 'created_at',
        direction: 'desc'
      };
    },
    // Enhanced Uploader Methods
    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase();
      
      switch (extension) {
        case 'pdf':
          return 'fas fa-file-pdf';
        case 'doc':
        case 'docx':
          return 'fas fa-file-word';
        case 'xls':
        case 'xlsx':
          return 'fas fa-file-excel';
        case 'ppt':
        case 'pptx':
          return 'fas fa-file-powerpoint';
        case 'txt':
          return 'fas fa-file-alt';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'fas fa-file-image';
        default:
          return 'fas fa-file';
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    triggerFileSelect() {
      if (!this.isUploading) {
        this.$refs.fileInput.click();
      }
    },
    
    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.selectedFile = file;
      }
    },
    
    removeFile(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.selectedFile = null;
      this.uploadError = null;
      this.uploadSuccess = false;
      
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    toggleNewSessionForm() {
      this.showNewSessionForm = !this.showNewSessionForm;
    },
    handleSessionDeleted(sessionId) {
      console.log(`Handling session deleted event for session ID: ${sessionId}`);
      
      try {
        // Remove from analysisSessions array
        const analysisIndex = this.analysisSessions.findIndex(s => s.id === sessionId);
        if (analysisIndex !== -1) {
          this.analysisSessions.splice(analysisIndex, 1);
          console.log(`Removed session ${sessionId} from analysisSessions array`);
        } else {
          console.warn(`Session ${sessionId} not found in analysisSessions array`);
        }
        
        // Also remove from main sessions array
        const sessionsIndex = this.sessions.findIndex(s => s.id === sessionId);
        if (sessionsIndex !== -1) {
          this.sessions.splice(sessionsIndex, 1);
          console.log(`Removed session ${sessionId} from sessions array`);
        } else {
          console.warn(`Session ${sessionId} not found in sessions array`);
        }
        
        // Clear active session if it was deleted
        if (this.activeAnalysisSession && this.activeAnalysisSession.id === sessionId) {
          console.log(`Clearing active session ${sessionId} as it has been deleted`);
          this.activeAnalysisSession = null;
        }
        
        // If we have any pending operations related to this session, cancel them
        if (this.pendingOperations && this.pendingOperations[sessionId]) {
          console.log(`Cancelling pending operations for deleted session ${sessionId}`);
          delete this.pendingOperations[sessionId];
        }
      } catch (error) {
        console.error(`Error handling session deletion for session ${sessionId}:`, error);
        // Try to recover by refreshing the sessions list
        this.tryRefreshSessions();
        
        // If there was an error, we might need to show a message to the user
        if (this.$toast) {
          this.$toast.warning('Error handling session deletion. Refreshing data...');
        }
      }
    },
    viewAnalysisSession(session) {
      // Check if session is valid
      if (!session || !session.id) {
        console.warn('Attempted to view an invalid session:', session);
        if (this.$toast) {
          this.$toast.error('Cannot view this session - invalid session data');
        }
        return;
      }
      
      console.log(`Viewing analysis session: ${session.id} - ${session.name}`);
      
      // Hide the new session form if it's visible
      if (this.showNewSessionForm) {
        console.log('Hiding new session form');
        this.showNewSessionForm = false;
      }
      
      // Set as active session to display session details
      console.log('Setting active session');
      this.activeAnalysisSession = session;
      
      // Get detailed session data with results
      this.fetchSessionDetails(session.id);
      
      // Use a more robust approach to scroll to session details after the DOM updates
      this.$nextTick(() => {
        console.log('DOM updated after session set, attempting to scroll');
        // Add a slightly longer delay to ensure everything is rendered and other components have initialized
        setTimeout(() => {
          const sessionDetails = document.getElementById('active-session-container');
          if (sessionDetails) {
            console.log('Found session details element, scrolling into view');
            
            // Make sure this element is visible first with direct style modifications
            sessionDetails.style.display = 'block';
            sessionDetails.style.opacity = '1';
            sessionDetails.style.visibility = 'visible';
            sessionDetails.style.position = 'relative';
            
            // Set focus to ensure keyboard accessibility and force browser attention
            sessionDetails.setAttribute('tabindex', '-1');
            sessionDetails.focus({preventScroll: true});
            
            // Then scroll to it with a more aggressive scroll behavior
            window.scrollTo({
              top: sessionDetails.offsetTop - 100, // Subtract some pixels to show context
              behavior: 'smooth'
            });
            
            // Add a brief highlight animation to draw attention
            sessionDetails.style.transition = 'background-color 0.5s ease';
            sessionDetails.style.backgroundColor = '#f0f9ff';
            setTimeout(() => {
              sessionDetails.style.backgroundColor = '';
            }, 1000);
          } else {
            console.warn('Could not find session details element to scroll to');
          }
        }, 300); // Longer delay to ensure DOM is fully updated
      });
    },
    shareAnalysisSession(session) {
      // Call API to share session with client
      api.post(`/projects/${this.projectId}/analysis-sessions/${session.id}/share/`)
        .then(response => {
          if (response && response.data) {
            this.handleSessionUpdated(response.data);
            if (this.$toast) {
              this.$toast.success('Session shared with client successfully');
            }
          }
        })
        .catch(error => {
          this.handleError(error, 'Failed to share session with client');
        });
    },
    exportAnalysisSession(session) {
      // Call API to export session as PDF or other format
      api.get(`/projects/${this.projectId}/analysis-sessions/${session.id}/export/`, {
        responseType: 'blob'
      })
        .then(response => {
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `analysis-session-${session.id}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          if (this.$toast) {
            this.$toast.success('Analysis report downloaded successfully');
          }
        })
        .catch(error => {
          this.handleError(error, 'Failed to export analysis report');
        });
    },
    async tryRefreshSessions() {
      console.log('Attempting to refresh sessions list after error');
      try {
        // Set a flag to prevent multiple refreshes
        if (this.isRefreshingSessions) {
          console.log('Already refreshing sessions, skipping duplicate request');
          return;
        }
        
        this.isRefreshingSessions = true;
        await this.fetchSessions();
        console.log('Successfully refreshed sessions list');
      } catch (error) {
        console.error('Error refreshing sessions:', error);
      } finally {
        this.isRefreshingSessions = false;
      }
    },
    isWorkflowRunning(run) {
      return (
        run.status === 'IN_PROGRESS' || run.status === 'PENDING' || run.status === 'PROCESSING'
      );
    },
    isSessionProcessing(session) {
      return (
        session.status === 'IN_PROGRESS' || session.status === 'PROCESSING' || session.status === 'PENDING'
      );
    },
    async fetchSessionDetails(sessionId) {
      console.log(`Fetching detailed data for session ${sessionId}`);
      
      try {
        // Use the same endpoint as the session list, but we shouldn't need this additional fetch
        // as we already have the session data from the list API call
        /*
        const response = await api.get(`/projects/${this.projectId}/analysis-sessions/${sessionId}/`);
        console.log('Session details response:', response);
        
        if (response && response.data) {
          // Update the active session with the detailed data
          const detailedSession = response.data.data || response.data;
          
          console.log('Detailed session data:', {
            id: detailedSession.id,
            name: detailedSession.name,
            analyses_count: detailedSession.analyses?.length || 0,
            has_analyses_with_results: detailedSession.analyses?.some(a => a.results) || false
          });
          
          // Only update if we still have the same session active
          if (this.activeAnalysisSession && this.activeAnalysisSession.id === sessionId) {
            console.log('Updating active session with detailed data');
            this.activeAnalysisSession = detailedSession;
          }
        }
        */
        
        // Since we already have the session data, let's just find the document IDs if they're missing
        if (this.activeAnalysisSession && this.activeAnalysisSession.analyses) {
          console.log('Checking for missing document IDs in analyses');
          
          // For each analysis that doesn't have a document_id, try to find it in the full analyses list
          this.activeAnalysisSession.analyses.forEach(analysis => {
            if (!analysis.document_id) {
              console.log(`Analysis ${analysis.id} has no document_id, looking up in analyses data`);
              
              // Check if we can find the analysis in our analyses data
              const fullAnalysisData = this.analyses?.find(a => a.id === analysis.id);
              if (fullAnalysisData && fullAnalysisData.document_id) {
                console.log(`Found document_id ${fullAnalysisData.document_id} for analysis ${analysis.id}`);
                analysis.document_id = fullAnalysisData.document_id;
              }
            }
          });
        }
      } catch (error) {
        console.error('Error fetching session details:', error);
        if (this.$toast) {
          this.$toast.warning('Could not fetch complete session details');
        }
      }
    },
  },
  beforeUnmount() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }
}
</script>

<style scoped>
.project-documents {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Breadcrumb styles */
.breadcrumbs {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.breadcrumb-item {
  color: #6c757d;
  text-decoration: none;
}

.breadcrumb-item:hover {
  color: #007bff;
  text-decoration: underline;
}

.breadcrumb-item.active {
  color: #343a40;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 0.5rem;
  color: #adb5bd;
}

/* Project Header */
.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px;
  background-color: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.project-info {
  flex: 1;
}

.page-title {
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  color: #212529;
}

.project-metadata {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.project-type {
  background-color: #e9ecef;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
}

.project-description {
  font-style: italic;
}

.document-types {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.label {
  color: #495057;
  font-weight: 500;
}

.type-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.type-tag {
  background-color: #f0f4f8;
  color: #495057;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.client-type {
  margin-left: auto;
  color: #6c757d;
}

/* Loading & Error States */
.loading, .error {
  padding: 2rem;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 1rem 0;
}

.loading {
  color: #6c757d;
}

.error {
  color: #dc3545;
}

/* Tabs */
.content-tabs {
  margin-bottom: 1.5rem;
}

.tab-buttons {
  display: flex;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1rem;
  gap: 0.25rem;
}

.tab-button {
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  font-size: 1rem;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-button:hover {
  color: #495057;
  border-bottom-color: #adb5bd;
}

.tab-button.active {
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 500;
}

.tab-button i {
  font-size: 0.9rem;
}

/* Tab Content */
.tab-content {
  padding: 1rem;
  background-color: #fff;
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Document Filters */
.document-filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.search-box {
  position: relative;
  flex-grow: 1;
}

.search-input {
  padding: 8px 12px 8px 36px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  width: 100%;
  font-size: 0.875rem;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
}

.filter-control, .sort-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-control label, .sort-control label {
  color: #4a5568;
  font-size: 0.875rem;
}

.filter-control select, .sort-control select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
}

.sort-direction-btn {
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Empty States */
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #a0aec0;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 15px;
}

.empty-state p {
  font-size: 1rem;
  margin: 0;
}

.help-text {
  color: #718096;
  font-size: 0.875rem;
  margin-bottom: 15px;
}

/* Analysis Sessions */
.sessions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.session-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.session-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.session-header h5 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #2d3748;
}

.session-info {
  margin-bottom: 10px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #718096;
  font-size: 0.875rem;
}

.info-item i {
  width: 16px;
  color: #4a5568;
}

.view-results-btn {
  width: 100%;
  background: #3182ce;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  transition: background 0.2s ease;
}

.view-results-btn:hover {
  background: #2c5282;
}

/* Loading and Error States */
.loading {
  text-align: center;
  padding: 20px;
  color: #3182ce;
  font-size: 1rem;
}

.error {
  text-align: center;
  padding: 20px;
  color: #e53e3e;
  background: #fff5f5;
  border-radius: 4px;
  font-size: 1rem;
}

/* Status Badges */
.status-badge {
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.completed {
  background: #c6f6d5;
  color: #22543d;
}

.status-badge.pending, 
.status-badge.draft {
  background: #fefcbf;
  color: #744210;
}

.status-badge.error,
.status-badge.failed {
  background: #fed7d7;
  color: #822727;
}

.status-badge.in_progress {
  background: #bee3f8;
  color: #2a4365;
}

/* Pending Upload Requests */
.requests-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.request-card {
  background: white;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.request-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.request-email {
  font-weight: 500;
  color: #2d3748;
}

.request-date {
  font-size: 0.875rem;
  color: #718096;
}

.request-details {
  margin-bottom: 12px;
}

.request-detail {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  margin-bottom: 4px;
  font-size: 0.875rem;
}

.request-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #edf2f7;
  color: #4a5568;
  transition: all 0.2s;
}

.action-button:hover {
  background: #e2e8f0;
}

.action-button.delete {
  background: #fed7d7;
  color: #c53030;
}

.action-button.delete:hover {
  background: #feb2b2;
}

/* Responsive Design */
@media (max-width: 768px) {
  .document-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box, .filter-control, .sort-control {
    width: 100%;
  }
  
  .tab-button {
    padding: 8px 12px;
    font-size: 0.875rem;
  }
  
  .sessions-grid, .requests-grid {
    grid-template-columns: 1fr;
  }
  
  .project-metadata {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* Analysis Item Styling */
.analysis-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 0.875rem;
}

.document-name {
  font-weight: 500;
  color: #2d3748;
}

/* Animation for transitions */
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.reset-filters-btn {
  background: #e2e8f0;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  color: #4a5568;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.reset-filters-btn:hover {
  background: #cbd5e0;
  color: #2d3748;
}

.reset-filters-btn i {
  color: #718096;
}

.results-counter {
  text-align: right;
  margin-top: 10px;
  color: #718096;
  font-size: 0.875rem;
}

.client-type {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: #4a5568;
  margin-left: 10px;
  padding-left: 10px;
  border-left: 1px solid #e2e8f0;
}

.client-specific-analysis {
  font-size: 0.875rem;
  color: #718096;
  margin-left: 10px;
  padding-left: 10px;
  border-left: 1px solid #e2e8f0;
}

/* Enhanced File Uploader */
.enhanced-uploader {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.drop-zone {
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f8fafc;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone.active {
  border-color: #3182ce;
  background: #ebf8ff;
}

.drop-zone.has-file {
  background: #f0f9ff;
  border-color: #90cdf4;
}

.drop-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #718096;
}

.drop-message i {
  font-size: 2.5rem;
  color: #a0aec0;
}

.drop-message p {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: #4a5568;
}

.file-hint {
  font-size: 0.75rem;
  color: #a0aec0;
}

.hidden-file-input {
  display: none;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
  padding: 10px;
}

.file-icon {
  width: 40px;
  height: 40px;
  background: #ebf8ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-icon i {
  font-size: 1.25rem;
  color: #3182ce;
}

.file-info {
  flex: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.file-name {
  font-weight: 500;
  color: #2d3748;
  font-size: 0.875rem;
  word-break: break-word;
}

.file-size {
  font-size: 0.75rem;
  color: #718096;
}

.remove-file {
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 5px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-file:hover {
  color: #e53e3e;
}

.upload-options {
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

.document-type-select {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.document-type-select label {
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 500;
}

.document-type-select select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #4a5568;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.document-type-select select:focus {
  border-color: #3182ce;
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.primary-button {
  background: #3182ce;
  color: white;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  min-width: 120px;
  justify-content: center;
}

.primary-button:hover:not(:disabled) {
  background: #2c5282;
}

.primary-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary-button.uploading {
  background: #2b6cb0;
}

.upload-progress-container {
  height: 6px;
  background: #edf2f7;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.upload-progress-bar {
  height: 100%;
  background: #3182ce;
  transition: width 0.3s ease;
}

.upload-progress-text {
  font-size: 0.75rem;
  color: #3182ce;
  text-align: right;
  margin-top: 4px;
}

.upload-error-message,
.upload-success-message {
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.upload-error-message {
  background: #fff5f5;
  color: #c53030;
  border: 1px solid #feb2b2;
}

.upload-success-message {
  background: #f0fff4;
  color: #2f855a;
  border: 1px solid #9ae6b4;
}

/* Responsive styles for enhanced uploader */
@media (max-width: 640px) {
  .upload-options {
    flex-direction: column;
    align-items: stretch;
  }
  
  .primary-button {
    width: 100%;
  }
}

.analysis-sessions-wrapper {
  margin-top: 20px;
}

.create-session-section {
  margin-bottom: 20px;
}

.new-session-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #3182ce;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.new-session-btn:hover {
  background-color: #2c5282;
}

.subsection-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

.active-session-details-wrapper {
  z-index: 10;
  position: relative;
  margin-top: 20px;
}

.active-session-details {
  border: 2px solid #4299e1;
  border-radius: 8px;
  padding: 24px;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 5;
  scroll-margin-top: 100px;
  min-height: 200px;
}

.active-session-details:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5), 0 4px 8px rgba(0, 0, 0, 0.1);
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.session-actions {
  display: flex;
  gap: 10px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #e2e8f0;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s;
}

.back-button:hover {
  background-color: #cbd5e0;
}

/* Transition animations */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Card styling */
.card {
  margin-bottom: 20px;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.card-header {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #2d3748;
}

.card-body {
  padding: 15px;
}

/* Project header styling */
.project-actions {
  display: flex;
  gap: 10px;
}

.btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  transition: background-color 0.2s, transform 0.1s;
}

.btn i {
  margin-right: 8px;
}

.btn-primary {
  background-color: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--color-secondary);
}

.btn:active {
  transform: translateY(1px);
}

/* === UNIFIED WORKSPACE STYLING === */

/* Main Workspace Container */
.discovery-analysis-workspace {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

/* Workspace Overview Section */
.workspace-overview {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.overview-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.stat-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 140px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-card i {
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.9);
}

.stat-content h3 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.stat-content p {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.quick-actions {
  display: flex;
  gap: 1rem;
}

.action-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  color: white;
  text-decoration: none;
}

.action-btn.primary {
  background: rgba(255, 255, 255, 0.9);
  color: #667eea;
  border-color: rgba(255, 255, 255, 0.9);
}

.action-btn.primary:hover {
  background: white;
  color: #667eea;
}

/* Workspace Sections */
.workspace-section {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-header h2 i {
  color: #667eea;
}

.section-description {
  color: #718096;
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
}

.client-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-left: 1rem;
}

/* Document Management Integration */
.document-management {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.subsection-header h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subsection-header h3 i {
  color: #667eea;
}

.subsection-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.batch-chunk-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: #ebf4ff;
  color: #3182ce;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.batch-chunk-btn:hover:not(:disabled) {
  background: #bee3f8;
  color: #2b6cb0;
}
.batch-chunk-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.document-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 15px;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Upload Workspace Improvements */
.upload-workspace {
  margin-bottom: 2rem;
}

.enhanced-uploader .drop-zone {
  border: 2px dashed #cbd5e0;
  border-radius: 15px;
  padding: 3rem;
  text-align: center;
  background: #f7fafc;
  transition: all 0.3s ease;
  cursor: pointer;
}

.enhanced-uploader .drop-zone:hover,
.enhanced-uploader .drop-zone.active {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.enhanced-uploader .drop-zone.has-file {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

/* Analysis Sessions Styling */
.analysis-sessions-wrapper {
  margin-top: 2rem;
}

.create-session-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f7fafc;
  border-radius: 15px;
  border: 1px solid #e2e8f0;
}

.new-session-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.new-session-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .workspace-overview {
    flex-direction: column;
    text-align: center;
  }
  
  .overview-stats {
    justify-content: center;
  }
  
  .workspace-section {
    padding: 1.5rem;
  }
  
  .subsection-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

/* Transitions for smooth experience */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* ==================== INTELLIGENCE HUB STYLES ==================== */

.intelligence-hub {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
}

/* Intelligence Overview */
.intelligence-overview {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.overview-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.overview-header h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.overview-description {
  font-size: 1rem;
  color: #718096;
  margin: 0;
}

/* Workflow Progress */
.workflow-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.phase-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  padding: 1.5rem;
  min-width: 240px;
  text-align: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.phase-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.phase-card.active::before {
  opacity: 1;
}

.phase-card.active {
  border-color: #667eea;
  transform: translateY(-4px);
  box-shadow: 0 15px 30px rgba(102, 126, 234, 0.2);
}

.phase-card.disabled {
  opacity: 0.6;
}

.contract-phase.active::before {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
}

.rag-phase.active::before {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.phase-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: white;
  position: relative;
  z-index: 1;
}

.contract-phase .phase-icon {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
}

.rag-phase .phase-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.phase-content h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
}

.phase-content p {
  color: #718096;
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
}

.phase-status .status {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

.status.pending {
  background: #fef3c7;
  color: #92400e;
}

.status.active {
  background: #d1fae5;
  color: #065f46;
}

.status.disabled {
  background: #f3f4f6;
  color: #9ca3af;
}

.workflow-arrow {
  font-size: 1.5rem;
  color: #cbd5e0;
  margin: 0 0.75rem;
}

/* Intelligence Sections Flexbox (Simplified) */
.intelligence-sections-flex {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
}

.intelligence-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  flex: 1;
  max-width: 50%;
  min-width: 300px;
}

.intelligence-card.disabled {
  opacity: 0.7;
  background: rgba(243, 244, 246, 0.95);
}

.section-header {
  margin-bottom: 1.5rem;
  text-align: center;
}

.section-header h2 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.section-header .section-description {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.5;
  margin: 0 auto;
}

/* Workspace Cards */
.contract-workspace,
.rag-workspace {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.workspace-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.workspace-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.workspace-card h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-description {
  color: #718096;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

/* Enhanced Uploaders */
.enhanced-uploader {
  margin-bottom: 1rem;
}

.drop-zone {
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.contract-drop {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.rag-drop {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.drop-zone.active {
  border-style: solid;
  transform: scale(1.02);
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.drop-zone-content i {
  font-size: 3rem;
  color: #cbd5e0;
}

.contract-drop i {
  color: #10b981;
}

.rag-drop i {
  color: #667eea;
}

.file-label {
  color: #667eea;
  cursor: pointer;
  text-decoration: underline;
}

.selected-file {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: #2d3748;
  font-weight: 600;
}

.clear-btn {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Upload Actions */
.upload-actions {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.upload-btn {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.upload-btn.primary {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  color: white;
}

.upload-btn.secondary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.upload-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.upload-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Requirements List */
.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.requirement-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(16, 185, 129, 0.05);
  border-radius: 10px;
  border-left: 4px solid #10b981;
}

.requirement-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.requirement-content h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.requirement-content p {
  color: #718096;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.show-more {
  text-align: center;
  margin-top: 1rem;
}

.btn-link {
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
}

/* Compliance Summary */
.compliance-summary {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.compliance-metric {
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(#10b981 var(--progress), #e2e8f0 var(--progress));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.metric-circle::before {
  content: '';
  position: absolute;
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  z-index: 1;
}

.metric-label {
  font-size: 0.8rem;
  color: #718096;
  z-index: 1;
}

.compliance-breakdown {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
}

.breakdown-item.passing {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.breakdown-item.failing {
  background: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.breakdown-item.pending {
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

/* Analysis Actions */
.analysis-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  color: inherit;
}

/* Disabled State */
.disabled-state {
  text-align: center;
  padding: 3rem;
  background: rgba(243, 244, 246, 0.5);
  border-radius: 15px;
  border: 2px dashed #d1d5db;
}

.disabled-content {
  max-width: 400px;
  margin: 0 auto;
}

.disabled-content i {
  font-size: 3rem;
  color: #9ca3af;
  margin-bottom: 1rem;
}

.disabled-content h4 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

.disabled-content p {
  color: #9ca3af;
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.btn-outline-primary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-outline-primary:hover {
  background: #667eea;
  color: white;
}

/* Document Workspace Section (Simplified) */
.document-workspace-section {
  width: 100%;
  max-width: 100%;
  margin-top: 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .intelligence-sections-flex {
    flex-direction: column;
    gap: 1rem;
  }
  
  .intelligence-card {
    max-width: 100%;
    min-width: auto;
  }
  
  .contract-workspace,
  .rag-workspace {
    grid-template-columns: 1fr;
  }
  
  .workflow-progress {
    flex-direction: column;
  }
  
  .workflow-arrow {
    transform: rotate(90deg);
  }
}

@media (max-width: 768px) {
  .intelligence-hub {
    padding: 1rem;
  }
  
  .intelligence-overview,
  .intelligence-section {
    padding: 1.5rem;
  }
  
  .overview-header h2 {
    font-size: 2rem;
  }
  
  .phase-card {
    min-width: auto;
    width: 100%;
  }
  
  .compliance-summary {
    flex-direction: column;
    text-align: center;
  }
}

/* Action Buttons and Status Cards */
.contract-actions,
.rag-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.action-description {
  text-align: center;
}

.action-description p {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.5;
  margin: 0;
}

.navigation-actions {
  display: flex;
  justify-content: center;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-btn.primary {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  color: white;
}

.action-btn.secondary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.action-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  text-decoration: none;
  color: inherit;
}

.action-btn.primary:hover {
  color: white;
}

.action-btn.secondary:hover {
  color: white;
}

/* Status Cards */
.contract-status,
.rag-status {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
}

.status-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  width: 100%;
  max-width: none;
}

.status-card.disabled {
  opacity: 0.7;
  background: rgba(243, 244, 246, 0.9);
}

.status-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.status-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: white;
  flex-shrink: 0;
}

.contract-status .status-icon {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
}

.rag-status .status-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.rag-status .status-card.disabled .status-icon {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
}

.status-content h4 {
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
}

.status-content p {
  color: #718096;
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.3;
}

/* Document Intelligence Actions Grid */
.document-intelligence-actions {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.actions-header {
  text-align: center;
  margin-bottom: 2rem;
}

.actions-header h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.actions-header p {
  color: #718096;
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.action-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.action-card .card-header {
  margin-bottom: 1.5rem;
  text-align: center;
}

.action-card .card-header h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.action-card .card-header p {
  color: #718096;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Upload Card Specific */
.upload-card .drop-zone {
  border: 2px dashed #10b981;
  background: rgba(16, 185, 129, 0.05);
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-card .drop-zone:hover,
.upload-card .drop-zone.active {
  border-style: solid;
  background: rgba(16, 185, 129, 0.1);
}

.upload-card .drop-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-card .drop-message i {
  font-size: 2rem;
  color: #10b981;
}

.upload-card .upload-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.upload-card .document-type-select select {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
}

.upload-card .primary-button {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.upload-card .primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* Classification Status */
.classification-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #e7f3ff;
  border-radius: 4px;
  color: #2196f3;
  font-size: 0.875rem;
}

.classification-status i {
  font-size: 1rem;
}

/* Request Card Specific */
.request-card .request-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.request-card .toggle-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.request-card .toggle-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.request-form-wrapper {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Analysis Card Specific */
.analysis-card .analysis-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.analysis-card .analysis-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.analysis-card .stat-item {
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.analysis-card .stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 0.25rem;
}

.analysis-card .stat-label {
  display: block;
  font-size: 0.8rem;
  color: #718096;
  font-weight: 500;
}

.analysis-card .toggle-button {
  background: linear-gradient(135deg, #f59e0b 0%, #92400e 100%);
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.analysis-card .toggle-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.session-form-wrapper {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Responsive */
@media (max-width: 1024px) {
  .actions-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .analysis-card .analysis-stats {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .document-intelligence-actions {
    padding: 1.5rem;
  }
  
  .actions-header h3 {
    font-size: 1.3rem;
    flex-direction: column;
    gap: 0.5rem;
  }
}

/* ==================== END INTELLIGENCE HUB STYLES ==================== */
</style> 